import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { API_URL, apiFetch } from '../../api/client';
import ControlPanel from '../../components/ControlPanel';
import QueryInfoBox from '../../components/QueryInfoBox';
import QueryLogLink from '../../components/QueryLogLink';
import AlphaFoldModelTable from '../../components/tables/AlphaFoldModelTable';
import ApoSitesTable from '../../components/tables/ApoSitesTable';
import { COLOR_PAIRS } from '../../components/tables/ColorPairs';
import HoloSitesTable from '../../components/tables/HoloSitesTable';
import QuerySitesTable from '../../components/tables/QuerySitesTable';
import UnobservedSitesTable from '../../components/tables/UnobservedSitesTable';
import {
   AhojProvider,
   AhojTableContext,
   fromControlState,
   fromStructureType,
   MolstarAction,
   StructureType,
} from '../../contexts/AhojContext';
import { useStructureEventBus } from '../../contexts/StructureEventBusContext';
import AllApoHoloSiteDto from '../../models/AllApoHoloSiteDto';
import { ApoHoloResultRow } from '../../models/ApoHoloResultRow';
import { ApoHoloSiteDto } from '../../models/ApoHoloSiteDto';
import { QueryDetail } from '../../models/QueryDetail';
import './query.css';
import VisualizationWindow from './VisualizationWindow';

const TYPE_TO_PANEL: Record<string, string> = {
   query_pocket: 'query',
   apo: 'apo',
   holo: 'holo',
   unobserved: 'unobserved',
   alphafold: 'af',
};

const AccordionEyeButton: React.FC<{ rows: ApoHoloResultRow[]; structureType: StructureType }> = ({
   rows,
   structureType,
}) => {
   const ctx = useContext(AhojTableContext);
   const handleClick = (e: React.MouseEvent): void => {
      e.stopPropagation();
      const anyVisible = rows.some((row) => ctx.controlsMap.get(row.row_id)?.isVisible);
      if (anyVisible) {
         rows
            .filter((row) => row.structure_file_url && ctx.controlsMap.has(row.row_id))
            .forEach((row) => {
               const currentState = ctx.controlsMap.get(row.row_id);
               const newState =
                  currentState === undefined ? fromStructureType(structureType) : fromControlState(currentState);
               newState.isVisible = false;
               newState.isSelected = false;
               ctx.setControl(row.row_id, newState);
               ctx.dispatch({
                  type: 'TOGGLE_VISIBILITY',
                  payload: { rowId: row.row_id, url: `${API_URL}/${row.structure_file_url}`, value: false },
               } as MolstarAction);
               ctx.dispatch({
                  type: 'FOCUS',
                  payload: { rowId: row.row_id, url: `${API_URL}/${row.structure_file_url}`, value: false },
               } as MolstarAction);
            });
      } else {
         const first20Ids = new Set(rows.slice(0, 20).map((r) => r.row_id));
         rows
            .filter((row) => first20Ids.has(row.row_id))
            .forEach((row) => {
               const currentState = ctx.controlsMap.get(row.row_id);
               const newState =
                  currentState === undefined ? fromStructureType(structureType) : fromControlState(currentState);
               newState.isVisible = true;
               newState.isSelected = false;
               ctx.setControl(row.row_id, newState);
               ctx.dispatch({
                  type: 'TOGGLE_VISIBILITY',
                  payload: { rowId: row.row_id, url: `${API_URL}/${row.structure_file_url}`, value: true },
               } as MolstarAction);
            });
      }
   };
   const anyVisible = rows.some((row) => ctx.controlsMap.get(row.row_id)?.isVisible);
   return (
      <button
         className={`accordion-header-eye${anyVisible ? ' accordion-header-eye--active' : ''}`}
         title="Show/hide first 20 structures"
         onClick={handleClick}
      >
         <img src="/icons/eye.svg" alt="show/hide" width="14" />
      </button>
   );
};

const PanelOpener: React.FC<{ openPanel: (panel: string) => void }> = ({ openPanel }) => {
   const { subscribeStructureClick } = useStructureEventBus();
   useEffect(() => {
      return subscribeStructureClick((rowDto) => {
         if (rowDto) {
            const panel = TYPE_TO_PANEL[rowDto.type];
            if (panel) openPanel(panel);
         }
      });
   }, [subscribeStructureClick, openPanel]);
   return null;
};

const Query = (): React.JSX.Element => {
   const location = useLocation();
   const [queryDetail, setQueryDetail] = useState<QueryDetail>();
   const [openPanels, setOpenPanels] = useState<Set<string>>(new Set(['query']));
   const leftPanelRef = useRef<HTMLDivElement>(null);
   const flexContainerRef = useRef<HTMLDivElement>(null);
   const isDragging = useRef(false);
   const dragType = useRef<'vertical' | 'horizontal'>('vertical');
   const dragStartX = useRef(0);
   const dragStartY = useRef(0);
   const dragStartWidth = useRef(0);
   const dragStartHeight = useRef(0);

   const onResizeStart = (e: React.MouseEvent): void => {
      isDragging.current = true;
      dragType.current = 'vertical';
      dragStartX.current = e.clientX;
      dragStartWidth.current = leftPanelRef.current?.offsetWidth ?? 600;
      e.preventDefault();
   };

   const onHorizontalResizeStart = (e: React.MouseEvent): void => {
      isDragging.current = true;
      dragType.current = 'horizontal';
      dragStartY.current = e.clientY;
      dragStartHeight.current = flexContainerRef.current?.offsetHeight ?? 400;
      e.preventDefault();
   };

   const fetchDetail = (): void => {
      apiFetch<QueryDetail>('/api/v2' + location.pathname, { credentials: 'include' })
         .then((data) => {
            setQueryDetail(data);
         })
         .catch((error) => {
            console.error(error);
            setQueryDetail(undefined);
         });
   };

   const colorPairCounts = useMemo(() => {
      const colorOptions = new Map<number, number>();
      for (let i = 0; i < COLOR_PAIRS.length; i++) {
         colorOptions.set(i, 0);
      }
      return colorOptions;
   }, []);

   const extractSitesForMolstarContext = (): AllApoHoloSiteDto => {
      const apo_head = queryDetail?.query.found_apo;
      const holo_head = queryDetail?.query.found_holo;
      const unobserved_head = queryDetail?.query.found_unobserved;
      const query_pockedts = queryDetail?.query.query_pockets;
      const alphafold = queryDetail?.query.found_alphafold;
      const apo_holo_head: AllApoHoloSiteDto = {
         apo_sites: mapToSiteDtos(apo_head || [], 'apo'),
         holo_sites: mapToSiteDtos(holo_head || [], 'holo'),
         unobserved_sites: mapToSiteDtos(unobserved_head || [], 'unobserved'),
         query_pocket_sites: mapToSiteDtos(query_pockedts || [], 'query_pocket'),
         alphafold_sites: mapToSiteDtos(alphafold || [], 'alphafold'),
      };

      return apo_holo_head;
   };

   const mapToSiteDtos = (
      apoHoloSiteRows: ApoHoloResultRow[],
      type: 'apo' | 'holo' | 'unobserved' | 'query_pocket' | 'alphafold',
   ): ApoHoloSiteDto[] => {
      const siteObjects = apoHoloSiteRows.map((row: ApoHoloResultRow): ApoHoloSiteDto => {
         return {
            id: row.row_id,
            url: row.structure_file_url ? `${API_URL}/${row.structure_file_url}` : '',
            type: type,
         } as ApoHoloSiteDto;
      });

      return siteObjects;
   };

   useEffect(() => {
      fetchDetail();
   }, [location.pathname]);

   const togglePanel = (panel: string): void => {
      setOpenPanels((prev) => {
         const next = new Set(prev);
         if (next.has(panel)) next.delete(panel);
         else next.add(panel);
         return next;
      });
   };

   const openPannelIfNotOpen = useCallback((panel: string): void => {
      setOpenPanels((prev) => {
         if (prev.has(panel)) return prev;
         const next = new Set(prev);
         next.add(panel);
         return next;
      });
   }, []);

   useEffect(() => {
      const handleMouseMove = (e: MouseEvent): void => {
         if (!isDragging.current) return;
         // If mouse button was released outside the window, cancel drag
         if (e.buttons === 0) {
            isDragging.current = false;
            return;
         }
         if (dragType.current === 'vertical') {
            const delta = e.clientX - dragStartX.current;
            const newWidth = Math.max(200, dragStartWidth.current + delta);
            if (leftPanelRef.current) {
               leftPanelRef.current.style.flex = `0 0 ${newWidth}px`;
               leftPanelRef.current.style.width = `${newWidth}px`;
            }
         } else {
            const delta = e.clientY - dragStartY.current;
            const newHeight = Math.max(150, dragStartHeight.current + delta);
            if (flexContainerRef.current) {
               flexContainerRef.current.style.height = `${newHeight}px`;
            }
         }
      };
      const handleMouseUp = (): void => {
         isDragging.current = false;
      };
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
         document.removeEventListener('mousemove', handleMouseMove);
         document.removeEventListener('mouseup', handleMouseUp);
      };
   }, []);

   return (
      <article id="query-result" className="md-content__inner md-typeset">
         {queryDetail && (
            <AhojProvider
               apoHoloSites={extractSitesForMolstarContext()}
               residueMapping={
                  queryDetail.query.residue_mapping instanceof Map
                     ? queryDetail.query.residue_mapping
                     : new Map(Object.entries(queryDetail.query.residue_mapping ?? {}))
               }
               initialSelectedPdb={{
                  pdbOrUniprotId: queryDetail.query.query_pockets?.[0]?.pdb_id,
                  rowId: queryDetail.query.query_pockets?.[0]?.row_id,
                  chainId: queryDetail.query.query_pockets?.[0]?.chains?.[0],
               }}
               initialSelectedUniprot={
                  queryDetail.query.query_pockets?.[0]?.uniprot_ids?.[0]
                     ? {
                          pdbOrUniprotId: queryDetail.query.query_pockets[0].uniprot_ids[0],
                          rowId: queryDetail.query.query_pockets[0].row_id,
                          chainId: queryDetail.query.query_pockets[0].chains?.[0],
                       }
                     : undefined
               }
               colorPairCounts={colorPairCounts}
            >
               <PanelOpener openPanel={openPannelIfNotOpen} />
               <div id="query-result-box">
                  <div className="flex-container" ref={flexContainerRef}>
                     <div
                        ref={leftPanelRef}
                        id="q-side-panel"
                        className="labeled-properties"
                        style={{ flex: '0 0 600px', width: 600 }}
                     >
                        {/* ── Compact header strip ── */}
                        <div className="query-info-strip">
                           <div className="query-title-row">
                              <span className="query-title-label">
                                 {queryDetail.is_db_entry ? 'DB Entry:' : 'Query:'}
                              </span>
                              <code className="query">{queryDetail.query.query}</code>
                              {!queryDetail.is_db_entry && (
                                 <a
                                    href={`/job/${String(queryDetail.id)}`}
                                    className="job-link"
                                    title="go to job results"
                                 >
                                    #{queryDetail.id}
                                 </a>
                              )}
                              <a
                                 href={`${API_URL}/${String(queryDetail.query.result_zip_url)}`}
                                 style={{ marginLeft: 'auto' }}
                              >
                                 <button className="btn btn-primary btn-dl">&#8595;&nbsp;Download</button>
                              </a>
                              {queryDetail.query.log_url && <QueryLogLink log_url={queryDetail.query.log_url} />}
                           </div>
                           {!queryDetail.is_db_entry && (
                              <div className="query-status-row">
                                 <span className="label">Status:</span>
                                 <span className={`status ${String(queryDetail.query.status)}`}>
                                    {queryDetail.query.status}
                                 </span>
                                 {!queryDetail.query.done && (
                                    <>
                                       &nbsp;(
                                       <a href="#" onClick={(): void => window.location.reload()}>
                                          reload
                                       </a>
                                       )
                                    </>
                                 )}
                              </div>
                           )}
                           {queryDetail.query.progress && !queryDetail.query.done && (
                              <div className="query-status-row">
                                 <span className="label">Progress:</span>
                                 <progress
                                    value={queryDetail.query.progress.processed}
                                    max={queryDetail.query.progress.total}
                                    title={`${String(queryDetail.query.progress.ratio)}%`}
                                 >
                                    ({queryDetail.query.progress?.processed}&nbsp;/&nbsp;
                                    {queryDetail.query.progress?.total})
                                 </progress>
                              </div>
                           )}
                           {queryDetail.query.comment && (
                              <div className="query-status-row">
                                 <span className="label">Note:</span>&nbsp;{queryDetail.query.comment}
                              </div>
                           )}
                        </div>

                        {/* ── Accordion panels ── */}
                        <div className="accordion-panels">
                           <div className="accordion-section">
                              <div className="accordion-header-row">
                                 <button className="accordion-header" onClick={(): void => togglePanel('info')}>
                                    <span className="accordion-triangle">{openPanels.has('info') ? '▼' : '▶'}</span>
                                    Query info
                                 </button>
                              </div>
                              {openPanels.has('info') && (
                                 <div className="accordion-body">
                                    <QueryInfoBox query={queryDetail.query} />
                                 </div>
                              )}
                           </div>
                           <div className="accordion-section">
                              <div className="accordion-header-row">
                                 <AccordionEyeButton
                                    rows={queryDetail.query.query_pockets ?? []}
                                    structureType="query_pocket"
                                 />
                                 <button className="accordion-header" onClick={(): void => togglePanel('query')}>
                                    <span className="accordion-triangle">{openPanels.has('query') ? '▼' : '▶'}</span>
                                    Query Pockets
                                    <span className="tab-count">{queryDetail.query.num_query_pockets}</span>
                                 </button>
                              </div>
                              {openPanels.has('query') && (
                                 <div className="accordion-body">
                                    <QuerySitesTable
                                       data={queryDetail.query}
                                       structureType="query_pocket"
                                       onSelectStructure={(): void => {
                                          openPannelIfNotOpen('query');
                                       }}
                                    />
                                 </div>
                              )}
                           </div>
                           <div className="accordion-section">
                              <div className="accordion-header-row">
                                 <AccordionEyeButton rows={queryDetail.query.found_apo ?? []} structureType="apo" />
                                 <button className="accordion-header" onClick={(): void => togglePanel('apo')}>
                                    <span className="accordion-triangle">{openPanels.has('apo') ? '▼' : '▶'}</span>
                                    APO Sites
                                    <span className="tab-count">{queryDetail.query.num_found_apo_chains}</span>
                                    {queryDetail.query.num_found_apo_pdbs > 0 && (
                                       <span className="tab-pdb">
                                          in {queryDetail.query.num_found_apo_pdbs} PDB{' '}
                                          {queryDetail.query.num_found_apo_pdbs === 1 ? 'entry' : 'entries'}
                                       </span>
                                    )}
                                 </button>
                              </div>
                              {openPanels.has('apo') && (
                                 <div className="accordion-body">
                                    <ApoSitesTable
                                       data={queryDetail.query}
                                       structureType="apo"
                                       onSelectStructure={(): void => {
                                          openPannelIfNotOpen('apo');
                                       }}
                                    />
                                 </div>
                              )}
                           </div>
                           <div className="accordion-section">
                              <div className="accordion-header-row">
                                 <AccordionEyeButton rows={queryDetail.query.found_holo ?? []} structureType="holo" />
                                 <button className="accordion-header" onClick={(): void => togglePanel('holo')}>
                                    <span className="accordion-triangle">{openPanels.has('holo') ? '▼' : '▶'}</span>
                                    Holo Sites
                                    <span className="tab-count">{queryDetail.query.num_found_holo_chains}</span>
                                    {queryDetail.query.num_found_holo_pdbs > 0 && (
                                       <span className="tab-pdb">
                                          in {queryDetail.query.num_found_holo_pdbs} PDB{' '}
                                          {queryDetail.query.num_found_holo_pdbs === 1 ? 'entry' : 'entries'}
                                       </span>
                                    )}
                                 </button>
                              </div>
                              {openPanels.has('holo') && (
                                 <div className="accordion-body">
                                    <HoloSitesTable
                                       data={queryDetail.query}
                                       structureType="holo"
                                       onSelectStructure={(): void => {
                                          openPannelIfNotOpen('holo');
                                       }}
                                    />
                                 </div>
                              )}
                           </div>
                           <div className="accordion-section">
                              <div className="accordion-header-row">
                                 <AccordionEyeButton
                                    rows={queryDetail.query.found_unobserved ?? []}
                                    structureType="unobserved"
                                 />
                                 <button className="accordion-header" onClick={(): void => togglePanel('unobserved')}>
                                    <span className="accordion-triangle">
                                       {openPanels.has('unobserved') ? '▼' : '▶'}
                                    </span>
                                    Unobserved Sites
                                    <span className="tab-count">{queryDetail.query.num_found_unobserved_chains}</span>
                                    {queryDetail.query.num_found_unobserved_pdbs > 0 && (
                                       <span className="tab-pdb">
                                          in {queryDetail.query.num_found_unobserved_pdbs} PDB{' '}
                                          {queryDetail.query.num_found_unobserved_pdbs === 1 ? 'entry' : 'entries'}
                                       </span>
                                    )}
                                 </button>
                              </div>
                              {openPanels.has('unobserved') && (
                                 <div className="accordion-body">
                                    <UnobservedSitesTable
                                       data={queryDetail.query}
                                       structureType="unobserved"
                                       onSelectStructure={(): void => openPannelIfNotOpen('unobserved')}
                                    />
                                 </div>
                              )}
                           </div>
                           <div className="accordion-section">
                              <div className="accordion-header-row">
                                 <AccordionEyeButton
                                    rows={queryDetail.query.found_alphafold ?? []}
                                    structureType="alphafold"
                                 />
                                 <button className="accordion-header" onClick={(): void => togglePanel('af')}>
                                    <span className="accordion-triangle">{openPanels.has('af') ? '▼' : '▶'}</span>
                                    AlphaFold Models
                                    <span className="tab-count">{queryDetail.query.num_af_sessions}</span>
                                 </button>
                              </div>
                              {openPanels.has('af') && (
                                 <div className="accordion-body">
                                    <AlphaFoldModelTable
                                       data={queryDetail.query}
                                       structureType="alphafold"
                                       onSelectStructure={(): void => openPannelIfNotOpen('af')}
                                    />
                                 </div>
                              )}
                           </div>
                        </div>
                     </div>

                     <div className="resize-handle" onMouseDown={onResizeStart} />
                     <div id="right-panel">
                        <VisualizationWindow query={queryDetail.query} />
                     </div>
                  </div>
                  <div className="horizontal-resize-handle" onMouseDown={onHorizontalResizeStart} />
                  <ControlPanel />
               </div>
            </AhojProvider>
         )}
      </article>
   );
};

export default Query;
